import React, { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";

const SignatureModal = ({
  isOpen,
  onClose,
  onSave,
  width = 500,
  height = 200,
}) => {
  const sigRef = useRef(null);
  const canvasWrapRef = useRef(null);

  // Actual pixel size the <canvas> will render at. HTML canvases need real
  // pixel dimensions (not %), so we measure the wrapper on mount/resize and
  // shrink the requested width/height to fit — keeping the aspect ratio.
  const [canvasSize, setCanvasSize] = useState({ width, height });

  useEffect(() => {
    if (!isOpen) return;

    const el = canvasWrapRef.current;
    if (!el) return;

    const aspectRatio = height / width;

    const computeSize = (availableWidth) => {
      const w = Math.max(220, Math.min(width, availableWidth));
      const h = Math.round(w * aspectRatio);
      setCanvasSize({ width: w, height: h });
    };

    // initial measure
    computeSize(el.clientWidth);

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        computeSize(entry.contentRect.width);
      }
    });
    observer.observe(el);

    return () => observer.disconnect();
  }, [isOpen, width, height]);

  const handleClear = () => {
    sigRef.current.clear();
  };

  const handleSave = () => {
    if (sigRef.current.isEmpty()) {
      alert("Please provide a signature first!");
      return;
    }

    const dataURL = sigRef.current.toDataURL("image/png");
    onSave(dataURL);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <h3 style={styles.title}>✍️ Add Signature</h3>
          <button onClick={onClose} style={styles.closeBtn} aria-label="Close">
            ✕
          </button>
        </div>

        {/* Canvas */}
        <div ref={canvasWrapRef} style={styles.canvasContainer}>
          <div
            style={{
              width: canvasSize.width,
              height: canvasSize.height,
              maxWidth: "100%",
            }}
          >
            <SignatureCanvas
              ref={sigRef}
              penColor="#000"
              canvasProps={{
                width: canvasSize.width,
                height: canvasSize.height,
                className: "sigCanvas",
                style: { width: "100%", height: "100%", touchAction: "none" },
              }}
            />
          </div>
        </div>

        {/* Actions */}
        <div style={styles.actions}>
          <button style={styles.clearBtn} onClick={handleClear}>
            Clear
          </button>

          <div style={styles.actionsRight}>
            <button style={styles.cancelBtn} onClick={onClose}>
              Cancel
            </button>

            <button style={styles.saveBtn} onClick={handleSave}>
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.55)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 999,
    backdropFilter: "blur(4px)",
    padding: "16px",
    boxSizing: "border-box",
  },

  modal: {
    background: "#ffffff",
    borderRadius: "12px",
    width: "100%",
    maxWidth: "560px",
    maxHeight: "calc(100vh - 32px)",
    overflowY: "auto",
    padding: "clamp(14px, 4vw, 20px)",
    boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
    animation: "fadeIn 0.2s ease",
    boxSizing: "border-box",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "15px",
    gap: "12px",
  },

  title: {
    margin: 0,
    fontSize: "clamp(1rem, 4vw, 1.25rem)",
  },

  closeBtn: {
    border: "none",
    background: "transparent",
    fontSize: "18px",
    cursor: "pointer",
    color: "#666",
    width: "36px",
    height: "36px",
    flexShrink: 0,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  canvasContainer: {
    border: "2px dashed #ccc",
    borderRadius: "8px",
    background: "#fafafa",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    boxSizing: "border-box",
    padding: "8px",
  },

  actions: {
    marginTop: "15px",
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
  },

  actionsRight: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    marginLeft: "auto",
  },

  clearBtn: {
    background: "#f5f5f5",
    border: "1px solid #ddd",
    padding: "8px 14px",
    borderRadius: "6px",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },

  cancelBtn: {
    background: "#eee",
    border: "none",
    padding: "8px 14px",
    borderRadius: "6px",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },

  saveBtn: {
    background: "#16a34a",
    color: "#fff",
    border: "none",
    padding: "8px 16px",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "500",
    whiteSpace: "nowrap",
  },
};

export default SignatureModal;
